import { Injectable } from '@angular/core';
import moment, {Moment, unitOfTime} from 'moment-timezone';
import {MessageTranslateService} from "@app/shared/services/i18n/translate-service-service.service";
import {DurationTuple} from "@shared/api/model/timestamp";
import {MeterIntervalUnits} from "@shared/services/api/meter-provider.interface";
import {I18nMessage} from "@shared/api/model/i18n";
/* In order to avoid circular dependency errors, the translate-service must be imported directly and not
 via the shared index.js e.g. import {MessageTranslateService} from '@shared/services'*/

export interface TimestampRangeOptions {
  startInclusive?: boolean;
  endInclusive?: boolean;
}

export enum IntervalDurationType {
  P, // interval has only date component and coarser (years, months, days)
  PT // interval has only time component and finer (hours, minutes, seconds)
}


export interface DurationDisplay {
  units: moment.unitOfTime.Base[];
  label: I18nMessage;
  labelAbbreviated: I18nMessage;
}


const durationDisplays: DurationDisplay[] = [{
  units: ['year', 'years', 'y'],
  label: {messageKey: 'Years'},
  labelAbbreviated: {messageKey: 'y'}
}, {
  units: ['month', 'months', 'M'],
  label: {messageKey: 'Months'},
  labelAbbreviated: {messageKey: 'M'}
}, {
  units: ['week', 'weeks', 'w'],
  label: {messageKey: 'Weeks'},
  labelAbbreviated: {messageKey: 'w'}
}, {
  units: ['day', 'days', 'd'],
  label: {messageKey: 'Days'},
  labelAbbreviated: {messageKey: 'd'}
}, {
  units: ['hour', 'hours', 'h'],
  label: {messageKey: 'Hours'},
  labelAbbreviated: {messageKey: 'h'}
}, {
  units: ['minute', 'minutes', 'm'],
  label: {messageKey: 'Minutes'},
  labelAbbreviated: {messageKey: 'm'}
}, {
  units: ['second', 'seconds', 's'],
  label: {messageKey: 'Seconds'},
  labelAbbreviated: {messageKey: 's'}
}
];


export const durationDisplaysByUnit: Map<unitOfTime.Base, DurationDisplay> = new Map();
durationDisplays.forEach(durationUnit => durationUnit.units.forEach(unit => durationDisplaysByUnit.set(unit, durationUnit)));


@Injectable({
  providedIn: 'root'
})
export class PeriodUtilService {

  private readonly durationRegex = /(\d+) years (\d+) mons (\d+) days (\d+) hours (\d+) mins (\d+\.\d+) secs/;
  private readonly momentDayUnits = new Set<moment.unitOfTime.Base>(['d', 'day', 'days']);

  constructor(private translateService: MessageTranslateService) {
  }

  private extractDurationByUnits(str: string): number[] {
    const [_, ...unitValues] = str.match(this.durationRegex);
    return unitValues.map(v => +v);
  }


  public generateTimestampSequence<T>(start: Moment, end: Moment, stepSize: number, stepUnit: moment.unitOfTime.Base): Moment[];
  public generateTimestampSequence<T>(start: Moment, end: Moment, stepSize: number, stepUnit: moment.unitOfTime.Base, options: TimestampRangeOptions): Moment[];
  // eslint-disable-next-line max-len
  public generateTimestampSequence<T>(start: Moment, end: Moment, stepSize: number, stepUnit: moment.unitOfTime.Base, options: TimestampRangeOptions, factoryFn: (ts: Moment) => T): T[];
  public generateTimestampSequence<T>(start: Moment, end: Moment, stepSize: number, stepUnit: moment.unitOfTime.Base,
                                      options: TimestampRangeOptions = { startInclusive: true, endInclusive: false },
                                      factoryFn: (ts: Moment) => any = (ts: Moment) => ts): T[] {
    if (start.isAfter(end)) {
      throw new Error('Parameter start cannot be later than parameter end.');
    }

    const tsSequence: T[] = [];
    if (options.startInclusive && start.isBefore(end)) {
      tsSequence.push(factoryFn(start.clone()));
    }

    const nextStepTs = start.clone().add(stepSize, stepUnit);
    while (nextStepTs.isBefore(end)) {
      tsSequence.push(factoryFn(nextStepTs.clone()));
      nextStepTs.add(stepSize, stepUnit);
    }

    // If endInclusive is true, only add the end timestamp if it is after the start timestamp,
    // or else (i.e. the start and end timestamps are equal) if startInclusive is true
    if (options.endInclusive && (end.isAfter(start) || options.startInclusive)) {
      tsSequence.push(factoryFn(end.clone()));
    }

    return tsSequence;
  }

  toDuration(str: string): moment.Duration | null {
    if (str) {
      if (str.startsWith('P')) {
        return moment.duration(str);
      }
      const [years, months, days, hours, minutes, seconds] = this.extractDurationByUnits(str);
      return moment.duration({ years, months, days, hours, minutes, seconds });
    } else {
      return null;
    }
  }

  intervalToMs(interval: string): number {
    const duration = this.toDuration(interval);
    return duration.asMilliseconds();
  }

  intervalToDurationTuple(intervalString: string): DurationTuple {
    const duration = this.toDuration(intervalString);
    const unit: moment.unitOfTime.Base = MeterIntervalUnits.find(_unit => duration[_unit] && duration[_unit]());
    return {
      amount: duration[unit](),
      unit
    };
  }

  toIsoString(str: string): string | null {
    return str ? this.toDuration(str).toISOString() : null;
  }

  toString(str: string): string | null {
    return str ? this.toDuration(str).humanize() : null;
  }

  // format duration to the best readable format, e.g., 360 mins => 6 hours
  // result is already i18n'ed
  formatDuration(duration: moment.Duration, dropEmptyUnit: boolean, finestGranularity: moment.unitOfTime.Base): string {
    if (duration == null) {
      return null;
    }

    const singularUnits: moment.unitOfTime.Base[] = ['year', 'month', 'day', 'hour', 'minute', 'second'];
    const units: moment.unitOfTime.Base[] = ['years', 'months', 'days', 'hours', 'minutes', 'seconds'];

    const output: string[] = [];
    let singularUnit: moment.unitOfTime.Base;
    for (let i = 0; i < units.length; i++) {
      const unit = units[i];
      singularUnit = singularUnits[i];

      const value = duration.get(unit);

      if (!dropEmptyUnit || value > 0) {
        output.push(value + ' ' + this.translateService.instant(value === 1 ? singularUnit : unit));
      }

      if (finestGranularity === unit || finestGranularity === singularUnit) {
        break;
      }
    }

    if (!output.length) {
      return this.translateService.instant('Less than a ' + singularUnit);
    } else if (output.length === 1) {
      return output[0];
    } else {
      const last = output.splice(output.length - 1, 1);
      return output.join(', ')
             + ' '
             + this.translateService.instant('and')
             + ' '
             + last;
    }
  }

  getDurationType(duration: moment.Duration): IntervalDurationType {
    return duration.toISOString().startsWith('PT') ? IntervalDurationType.PT : IntervalDurationType.P;
  }

  getIntervalDurationType(interval: string): IntervalDurationType {
    if (interval == null) {
      return null;
    }

    const duration = this.toDuration(interval);
    if (duration == null) {
      return null;
    }

    return this.getDurationType(duration);
  }

  private getMinutesAgoDisplayText(minutes: number, compact: boolean): string {
    return minutes === 1
      ? this.translateService.instant(compact ? '1 minute' : '1 minute ago')
      : this.translateService.instant(compact ? 'n minutes' : 'n minutes ago', {minutes});
  }

  private getHoursAgoDisplayText(hours: number, compact: boolean): string {
    return hours === 1
      ? this.translateService.instant(compact ? '1 hour' : '1 hour ago')
      : this.translateService.instant(compact ? 'n hours' : 'n hours ago', {hours});
  }

  private getDaysAgoDisplayText(days: number, compact: boolean): string {
    return days === 1
      ? this.translateService.instant(compact ? '1 day' : '1 day ago')
      : this.translateService.instant(compact ? 'n days' : 'n days ago', {days});
  }

  private getWeeksAgoDisplayText(weeks: number, compact: boolean): string {
    return weeks === 1
      ? this.translateService.instant(compact ? '1 week' : '1 week ago')
      : this.translateService.instant(compact ? 'n weeks' : 'n weeks ago', {weeks});
  }

  private getMonthsAgoDisplayText(months: number, compact: boolean): string {
    return months === 1
      ? this.translateService.instant(compact ? '1 month' : '1 month ago')
      : this.translateService.instant(compact ? 'n months' : 'n months ago', {months});
  }

  private getYearsAgoDisplayText(years: number, compact: boolean): string {
    return years === 1
      ? this.translateService.instant(compact ? '1 year' : '1 year ago')
      : this.translateService.instant(compact ? 'n years' : 'n years ago', {years});
  }

  timeAgo(pastDate: moment.Moment, compact?: boolean): string {
    const currentDate: moment.Moment = pastDate.tz() ? moment.tz(pastDate.tz()) : moment();
    const duration = moment.duration(currentDate.diff(pastDate));

    if (duration.asSeconds() < 10) {
      // do not consider compact flag because if we display only "moments" it'll look odd.
      return this.translateService.instant('moments ago');
    } else if (duration.asMinutes() < 1) {
      return this.translateService.instant(compact ? 'less than a minute' : 'less than a minute ago');
    } else if (duration.asHours() < 1) {
      return this.getMinutesAgoDisplayText(Math.round(duration.asMinutes()), compact);
    } else if (duration.asDays() < 1) {
      return this.getHoursAgoDisplayText(Math.round(duration.asHours()), compact);
    } else if (duration.asWeeks() < 1) {
      return this.getDaysAgoDisplayText(Math.round(duration.asDays()), compact);
      /** Using `moment.diff` here so that it is aware of the length of last month. If it sees that the days are
       * at least the size of last month, then it counts it as 1 month. In case of March, when the difference to
       * another moment is 28 or 29 days, it counts it as 1 month because it is the size of February.
       * If `moment.duration` was used, it would only count 1 month when the amount of days is somewhere between 30-31
       * but that amount when subtracted from March could skip February and mean January which is not really just 1 month. */
    } else if (currentDate.diff(pastDate, 'month') < 1) {
      const movingDate = currentDate.clone();
      let weeks = movingDate.diff(pastDate, 'weeks');
      movingDate.subtract(weeks, 'weeks');
      let days = Math.round(movingDate.diff(pastDate, 'days', true));
      if (days === 7) {
        days = 0;
        weeks++;
      }
      return weeks == 1 && days === 0 ? this.getWeeksAgoDisplayText(1, compact)
        : weeks == 1 && days === 1 ? this.translateService.instant(compact ? '1 week and 1 day' : '1 week and 1 day ago')
        : weeks == 1 && days > 1 ? this.translateService.instant(compact ? '1 week and n days' : '1 week and n days ago', {days: days})
        : weeks > 1 && days === 0 ? this.getWeeksAgoDisplayText(weeks, compact)
        : weeks > 1 && days === 1 ? this.translateService.instant(compact ? 'n weeks and 1 day' : 'n weeks and 1 day ago', {weeks: weeks})
        : this.translateService.instant(compact ? 'n weeks and n days' : 'n weeks and n days ago', {weeks: weeks, days: days});
    } else if (currentDate.diff(pastDate, 'year') < 1) {
      const movingDate = currentDate.clone();
      let months = movingDate.diff(pastDate, 'months');
      movingDate.subtract(months, 'months');
      let days = Math.round(movingDate.diff(pastDate, 'days', true));
      return months == 1 && days === 0 ? this.getMonthsAgoDisplayText(1, compact)
        : months == 1 && days === 1 ? this.translateService.instant(compact ? '1 month and 1 day' : '1 month and 1 day ago')
        : months == 1 && days > 1 ? this.translateService.instant(compact ? '1 month and n days' : '1 month and n days ago', {days: days})
        : months > 1 && days === 0 ? this.getMonthsAgoDisplayText(months, compact)
        : months > 1 && days === 1 ? this.translateService.instant(compact ? 'n months and 1 day' : 'n months and 1 day ago', {months: months})
        : this.translateService.instant(compact ? 'n months and n days' : 'n months and n days ago', {months: months, days: days});

    } else {
      const movingDate = currentDate.clone();
      let years = movingDate.diff(pastDate, 'years');
      movingDate.subtract(years, 'years');
      let months = Math.round(movingDate.diff(pastDate, 'months', true));
      if (months === 12) {
        months = 0;
        years++;
      }
      return years == 1 && months === 0 ? this.getYearsAgoDisplayText(1, compact)
        : years == 1 && months === 1 ? this.translateService.instant(compact ? '1 year and 1 month' : '1 year and 1 month ago')
        : years == 1 && months > 1 ? this.translateService.instant(compact ? '1 year and n months' : '1 year and n months ago', {months: months})
        : years > 1 && months === 0 ? this.getYearsAgoDisplayText(years, compact)
        : years > 1 && months === 1 ? this.translateService.instant(compact ? 'n years and 1 month' : 'n years and 1 month ago', {years: years})
        : this.translateService.instant(compact ? 'n years and n months' : 'n years and n months ago' , {years: years, months: months});
    }
  }

  timeAgoAbbreviated(pastDate: moment.Moment, compact: boolean = false): string | null {
    if (pastDate) {
      //Only returns the most significant elapsed time component e.g. minutes, hours, days, weeks months OR years
      const currentDate: moment.Moment = pastDate.tz() ? moment.tz(pastDate.tz()) : moment();
      const duration = moment.duration(currentDate.diff(pastDate));

      if (duration.asSeconds() < 10) {
        return this.translateService.instant('moments ago');
      } else if (duration.asHours() < 1) {
        return this.getMinutesAgoDisplayText(Math.round(duration.asMinutes()), compact);

      } else if (duration.asDays() < 1) {
        return this.getHoursAgoDisplayText(Math.round(duration.asHours()), compact);
      } else if (duration.asWeeks() < 1) {
        return this.getDaysAgoDisplayText(Math.round(duration.asDays()), compact);
        /** Using `moment.diff` here so that it is aware of the length of last month. If it sees that the days are
         * at least the size of last month, then it counts it as 1 month. In case of March, when the difference to
         * another moment is 28 or 29 days, it counts it as 1 month because it is the size of February.
         * If `moment.duration` was used, it would only count 1 month when the amount of days is somewhere between 30-31
         * but that amount when subtracted from March could skip February and mean January which is not really just 1 month. */
      } else if (currentDate.diff(pastDate, 'month') < 1) {
        const movingDate: moment.Moment = currentDate.clone();
        let weeks: number = movingDate.diff(pastDate, 'weeks');
        movingDate.subtract(weeks, 'weeks');
        let days: number = Math.round(movingDate.diff(pastDate, 'days', true));
        if (days === 7) {
          weeks++;
        }
        return this.getWeeksAgoDisplayText(weeks, compact);

      } else if (currentDate.diff(pastDate, 'year') < 1) {
        const movingDate: moment.Moment = currentDate.clone();
        let months: number = movingDate.diff(pastDate, 'months');
        movingDate.subtract(months, 'months');
        return this.getMonthsAgoDisplayText(months, compact);
      } else {
        const movingDate: moment.Moment = currentDate.clone();
        let years: number = movingDate.diff(pastDate, 'years');
        movingDate.subtract(years, 'years');
        const months: number = Math.round(movingDate.diff(pastDate, 'months', true));
        if (months === 12) {
          years++;
        }
        return this.getYearsAgoDisplayText(years, compact);
      }
    } else {
      return null;
    }

  }

  sparsestDuration(durations: moment.Duration[]): moment.Duration | null {
    return durations?.length
      ? durations.sort((a, b) => (b?.asMilliseconds() ?? 0) - (a?.asMilliseconds() ?? 0))[0]
      : null;
  }

  roundDateTime(ts: Moment, period: DurationTuple): Moment {
    // momentjs uses 'day' or 'days' or 'd' as unit instead of 'date' for duration
    // when we are trying to later on use this unit to extract the date part from a moment object
    // get('day') yields the day of week, get('date') yields the date of month
    // therefore we convert the unit to 'date' here.
    const periodComponent = this.momentDayUnits.has(period.unit) ? 'date' : period.unit;
    if (period.amount === 1) {
      return ts.clone().startOf(periodComponent);
    } else {
      const roundedNumber = Math.floor(ts.get(periodComponent) / period.amount) * period.amount;
      return ts.clone().set(periodComponent, roundedNumber).startOf(periodComponent);
    }
  }

  // @deprecated
  roundDateTimeToPeriod(ts: moment.Moment, period: number, unit: moment.unitOfTime.Base): moment.Moment {
    return this.roundDateTime(ts, {unit, amount: period});
  }

}
